"use client";

import { useState } from "react";
import { trackLead } from "@/lib/analytics";

// Generisk smakebit-boks for sider uten én bestemt oppskrift (/oppskrifter-indeksen).
// Samme magnet som RecipeEmailCta, men leverer via /api/smakebit (PDF-en direkte).
export function SmakebitCta() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      // Verdibyttet (gratis PDF + løpende oppskrifter/tips) er tydelig opplyst
      // ved knappen, så innsending er informert, aktivt samtykke.
      const res = await fetch("/api/smakebit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, consent: true, utm: { source: "oppskrifter-indeks" } }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || "Noe gikk galt");
      } else {
        setDownloadUrl(json.downloadUrl || "");
        trackLead("smakebit_download", { email, method: "oppskrifter-indeks" });
      }
    } catch {
      setError("Kunne ikke sende. Prøv igjen om litt.");
    } finally {
      setLoading(false);
    }
  }

  if (downloadUrl) {
    return (
      <div className="rounded-3xl border border-green-200/60 bg-gradient-to-br from-green-50 via-emerald-50/70 to-amber-50 p-7 sm:p-9 shadow-sm text-center">
        <h3 className="text-lg font-semibold text-stone-900 mb-1.5">PDF-en er på vei til innboksen!</h3>
        <p className="text-sm text-stone-600 max-w-md mx-auto mb-4">Eller last den ned med én gang:</p>
        <a
          href={downloadUrl}
          className="inline-block px-6 py-3 bg-amber-700 text-white rounded-xl text-sm font-semibold hover:bg-amber-800 transition-all"
        >
          Last ned ukesmenyen (PDF) →
        </a>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-stone-200/70 bg-gradient-to-br from-amber-50/80 via-orange-50/40 to-stone-50 p-7 sm:p-9 shadow-sm relative overflow-hidden">
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-amber-200/40 to-orange-200/20 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="relative z-10">
        <span className="text-xs font-medium text-stone-700 uppercase tracking-wide">Gratis ukesmeny · PDF</span>
        <h3 className="text-xl sm:text-2xl font-bold text-stone-900 leading-tight mt-2 mb-2">
          7 middager under 500 kcal <span className="italic text-stone-500 font-normal">— med ferdig handleliste</span>
        </h3>
        <p className="text-sm text-stone-600 mb-5 max-w-lg">
          Én handletur, sju middager som metter. Rett i innboksen — helt gratis.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Fornavn (valgfritt)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white/80 backdrop-blur-sm border border-stone-200/70 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400 placeholder:text-stone-400"
            />
            <input
              type="email"
              required
              placeholder="din@epost.no"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/80 backdrop-blur-sm border border-stone-200/70 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400 placeholder:text-stone-400"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center pt-1">
            <button
              type="submit"
              disabled={!email || loading}
              className="px-6 py-3 bg-stone-900 text-white rounded-xl text-sm font-semibold hover:bg-stone-800 transition-all shadow-sm shadow-stone-900/10 disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-initial"
            >
              {loading ? "Sender …" : "Send meg PDF-en gratis →"}
            </button>
            <p className="text-xs text-stone-400 sm:ml-3">Vi deler aldri e-posten din.</p>
          </div>
          <p className="text-xs text-stone-500 leading-relaxed pt-0.5 max-w-lg">
            Du får PDF-en med én gang — pluss nye oppskrifter og kostholds-tips fra CoachGuiden (maks 1–2 i uka). Meld deg av når som helst.
          </p>
        </form>
        {error && (
          <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5 text-xs text-red-700">{error}</div>
        )}
      </div>
    </div>
  );
}
